import i18n from '@/utils/i18n';
import React, { useCallback, useMemo } from 'react';
import { AudioPlayer } from '@/components/audio-player';
import {
  getSegmentsForAttachmentIndex,
  mapSegmentsToTextSegments,
} from './utils';
import { LIST_GAP_HORIZONTAL } from '@/styles';
import { OrderMetadataSegment } from './types';
import { OriginalInboundTimestamps } from './original-inbound-timestamps';
import { StyleSheet, View } from 'react-native';
import { useAudioPlayer } from '@/hooks/use-audio-player';

interface Props {
  attachmentIndex: number;
  audioUrl: string;
  messageHeader?: React.ReactNode;
  onSelect?: (searchInputUUID: string) => void;
  orderUUID: string;
  segments: OrderMetadataSegment[];
  selectedInputUUID?: string;
  showTranslation?: boolean;
}

export const OriginalInboundAudio: React.FC<Props> = ({
  attachmentIndex,
  audioUrl,
  messageHeader,
  onSelect,
  orderUUID,
  segments,
  selectedInputUUID,
  showTranslation,
}) => {
  const audioPlayerState = useAudioPlayer(audioUrl);

  const textSegments = useMemo(() => {
    const filteredSegments = getSegmentsForAttachmentIndex(
      segments,
      attachmentIndex,
    );
    return mapSegmentsToTextSegments(
      filteredSegments,
      showTranslation ? i18n.locale : undefined,
    );
  }, [segments, attachmentIndex, showTranslation]);

  const onSelectCallback = useCallback(
    (searchInputUUID: string, timestampMS: number | null) => {
      if (typeof timestampMS === 'number') {
        audioPlayerState.setPosition(timestampMS);
      }
      onSelect?.(searchInputUUID);
    },
    [audioPlayerState, onSelect],
  );

  return (
    <>
      <View style={styles.audioPlayerContainer}>
        <AudioPlayer audioPlayerState={audioPlayerState} />
      </View>
      {messageHeader}
      {textSegments.length > 0 ? (
        <OriginalInboundTimestamps
          audioPlayerState={audioPlayerState}
          keyPrefix={`${orderUUID}-audio-${attachmentIndex}`}
          onSelect={onSelectCallback}
          segments={textSegments}
          selectedInputUUID={selectedInputUUID}
        />
      ) : null}
    </>
  );
};

const styles = StyleSheet.create({
  audioPlayerContainer: {
    marginBottom: 16,
    marginTop: LIST_GAP_HORIZONTAL,
  },
});
